import { object, string, array } from 'yup'
import _ from 'lodash'
import {
  setExaminersFormError,
  setFormError,
  setReduxValue,
  setFormNextButtonDisabled,
} from '.'
import { checkIdCard, isValidateDate, saveDebounce } from 'src/utils/lib'
import { userType } from 'src/components/UpdateProfileDialog/UpdateProfileForm/model'

export async function handleFormInput(
  currentState,
  originalState,
  index,
  listenerApi
) {
  const otherDocConfig = listenerApi.getState().eExamination?.otherDocConfig

  // Get user type from localStorage to determine role
  const user = JSON.parse(localStorage.getItem('user') ?? '{}')
  const isAgent = user?.userType === userType.AGENT
  const staffType = _.get(user, 'staffProfile.type', null)
  const nonAcademy = staffType === 'NON_ACADEMY'
  const isStaffAcademy =
    user?.userType === userType.STAFF && staffType !== 'NON_ACADEMY'
  const isStaffNonAcademy =
    user?.userType === userType.STAFF && staffType === 'NON_ACADEMY'
  const isProspect = user?.userType === userType.PROSPECT

  // Staff Non Academy and Prospect: All fields required except email
  const isAllFieldsRequired = isStaffNonAcademy || isProspect

  let validateInputSchema = object().shape({
    prefix: string().when([], {
      is: () => {
        // Agent: required, Staff Non Academy: required, Prospect: required, others: not validated
        return isAgent || isAllFieldsRequired
      },
      then: string().required('กรุณาเลือกคำนำหน้าชื่อ'),
      otherwise: string().nullable(),
    }),
    firstNameTH: string().when([], {
      is: () => {
        // Staff Non Academy: required, Prospect: required, others: not validated
        return isAllFieldsRequired
      },
      then: string().required('กรุณากรอกชื่อผู้เข้าสอบ'),
      otherwise: string().nullable(),
    }),
    lastNameTH: string().when([], {
      is: () => {
        // Staff Non Academy: required, Prospect: required, others: not validated
        return isAllFieldsRequired
      },
      then: string().required('กรุณากรอกนามสกุลผู้เข้าสอบ'),
      otherwise: string().nullable(),
    }),
    idCardNo: string().when([], {
      is: () => {
        // Staff Academy: required, Staff Non Academy: required, Prospect: required, Non Academy: required
        return isStaffAcademy || isStaffNonAcademy || isProspect || nonAcademy
      },
      then: string().required('เลขประจำตัวประชาชน'),
      otherwise: string().nullable(),
    }),
    mobileNo: string().when([], {
      is: () => {
        // Staff Non Academy: required, Prospect: required, Non Academy: required
        return isAllFieldsRequired || nonAcademy
      },
      then: string().required('กรุณากรอกหมายเลขโทรศัพท์มือถือ'),
      otherwise: string().nullable(),
    }),
    birthDate: string().when([], {
      is: () => {
        // Staff Academy: required, Staff Non Academy: required, Prospect: required, Non Academy: required
        return isStaffAcademy || isStaffNonAcademy || isProspect || nonAcademy
      },
      then: string().required('กรุณาเลือกวัน เดือน ปี เกิด'),
      otherwise: string().nullable(),
    }),
    gender: string().when([], {
      is: () => {
        // Staff Non Academy: required, Prospect: required, others: not validated
        return isAllFieldsRequired
      },
      then: string().when('prefix', {
        is: (e) => e?.length > 1,
        then: string().required('กรุณาเลือกเพศ'),
      }),
      otherwise: string().nullable(),
    }),
    zipCode: string().when([], {
      is: () => {
        // Staff Non Academy: required, Prospect: required, others: not validated
        return isAllFieldsRequired
      },
      then: string().required('กรุณากรอกรหัสไปรษณีย์'),
      otherwise: string().nullable(),
    }),
    province: string().when([], {
      is: () => {
        // Staff Non Academy: required, Prospect: required, others: not validated
        return isAllFieldsRequired
      },
      then: string().required('กรุณากรอกจังหวัด'),
      otherwise: string().nullable(),
    }),
    amphure: string().when([], {
      is: () => {
        // Staff Non Academy: required, Prospect: required, others: not validated
        return isAllFieldsRequired
      },
      then: string().required('กรุณากรอกอำเภอ/เขต'),
      otherwise: string().nullable(),
    }),
    tambon: string().when([], {
      is: () => {
        // Staff Non Academy: required, Prospect: required, others: not validated
        return isAllFieldsRequired
      },
      then: string().required('กรุณากรอกตำบล/แขวง'),
      otherwise: string().nullable(),
    }),
    address: string().when([], {
      is: () => {
        // Staff Non Academy: required, Prospect: required, others: not validated
        return isAllFieldsRequired
      },
      then: string().required('กรุณากรอกรายละเอียดที่อยู่'),
      otherwise: string().nullable(),
    }),
    institution: string().when([], {
      is: () => {
        // Staff Non Academy: required, Prospect: required, others: not validated
        return isAllFieldsRequired
      },
      then: string().required('กรุณากรอกสถานศึกษา'),
      otherwise: string().nullable(),
    }),
    qualification: string().when([], {
      is: () => {
        // Staff Non Academy: required, Prospect: required, others: not validated
        return isAllFieldsRequired
      },
      then: string().required('กรุณาเลือกวุฒิการศึกษา'),
      otherwise: string().nullable(),
    }),
    branch: string().when([], {
      is: () => {
        // Staff Non Academy: required, Prospect: required, others: not validated
        return isAllFieldsRequired
      },
      then: string().required('กรุณากรอกสาขา'),
      otherwise: string().nullable(),
    }),
    cardImage: object()
      .shape({
        cardImage: string().required('กรุณาอัปโหลดรูปหน้าบัตรประชาชน'),
      })
      .required('กรุณาอัปโหลดรูปหน้าบัตรประชาชน'),
    otherDocs: array().when([], {
      is: () => otherDocConfig && otherDocConfig?.config?.status === 'ACTIVE',
      then: array()
        .test('other-docs', 'กรุณาอัปโหลดเอกสาร', (val) => {
          return val?.length >= 1
        })
        .max(5, 'อัพโหลดได้สูงสุด 5 ไฟล์'),
      otherwise: array().nullable().min(0).max(5, 'อัพโหลดได้สูงสุด 5 ไฟล์'),
    }),
  })

  try {
    await validateInputSchema.validate(currentState, { abortEarly: false })
    if (originalState.formNextButtonDisabled[index]) {
      listenerApi.dispatch(
        setFormNextButtonDisabled({ index: index, value: false })
      )
    }
  } catch (e) {
    if (!originalState.formNextButtonDisabled[index]) {
      listenerApi.dispatch(
        setFormNextButtonDisabled({ index: index, value: true })
      )
    }
  }
}

export async function handleFormNextStep(
  currentState,
  originalState,
  index,
  listenerApi
) {
  // Get user type from localStorage to determine role
  const user = JSON.parse(localStorage.getItem('user') ?? '{}')
  const isAgent = user?.userType === userType.AGENT
  const staffType = _.get(user, 'staffProfile.type', null)
  const nonAcademy = staffType === 'NON_ACADEMY'
  const isStaffAcademy =
    user?.userType === userType.STAFF && staffType !== 'NON_ACADEMY'
  const isStaffNonAcademy =
    user?.userType === userType.STAFF && staffType === 'NON_ACADEMY'
  const isProspect = user?.userType === userType.PROSPECT

  // Staff Non Academy and Prospect: All fields required except email
  const isAllFieldsRequired = isStaffNonAcademy || isProspect

  // Helper function to check if date is before today
  const isDateBeforeToday = (dateStr) => {
    if (!dateStr) return false
    const date = new Date(dateStr)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }

  let validateInputSchema = object().shape({
    prefix: string()
      .default('')
      .when('$isAgent', {
        is: true,
        then: string().required('กรุณาเลือกคำนำหน้าชื่อ'),
        otherwise: string().when('$isAllFieldsRequired', {
          is: true,
          then: string().required('กรุณาเลือกคำนำหน้าชื่อ'),
          otherwise: string().nullable(),
        }),
      }),
    firstNameTH: string()
      .default('')
      .when('$isAgent', {
        is: true,
        then: string().nullable(), // AGENT: ไม่ต้อง validate
        otherwise: string().when('$isAllFieldsRequired', {
          is: true,
          then: string()
            .required('กรุณากรอกชื่อผู้เข้าสอบ')
            .test(
              'firstNameTH-length-test',
              'กรอกได้ไม่เกิน 255 ตัวอักษร',
              (val) => {
                return val?.length <= 255
              }
            ),
          otherwise: string()
            .test(
              'firstNameTH-length-test',
              'กรอกได้ไม่เกิน 255 ตัวอักษร',
              (val) => {
                return !val || val?.length <= 255
              }
            )
            .nullable(),
        }),
      }),
    lastNameTH: string()
      .default('')
      .when('$isAgent', {
        is: true,
        then: string().nullable(), // AGENT: ไม่ต้อง validate
        otherwise: string().when('$isAllFieldsRequired', {
          is: true,
          then: string()
            .required('กรุณากรอกนามสกุลผู้เข้าสอบ')
            .test(
              'lastNameTH-length-test',
              'กรอกได้ไม่เกิน 255 ตัวอักษร',
              (val) => {
                return val?.length <= 255
              }
            ),
          otherwise: string()
            .test(
              'lastNameTH-length-test',
              'กรอกได้ไม่เกิน 255 ตัวอักษร',
              (val) => {
                return !val || val?.length <= 255
              }
            )
            .nullable(),
        }),
      }),
    idCardNo: string()
      .default('')
      .when('$isNeedIdCard', {
        is: true,
        then: string()
          .required('กรุณากรอกเลขประจำตัวประชาชน')
          .test('idCardNo-length-test', 'กรอกได้ไม่เกิน 13 ตัวอักษร', (val) => {
            return val?.length <= 13
          })
          .test(
            'idCardNo-test',
            'กรุณากรอกเลขประจำตัวประชาชนให้ถูกต้อง',
            (val) => {
              return checkIdCard(val)
            }
          ),
        otherwise: string().nullable(),
      }),
    mobileNo: string()
      .default('')
      .when('$isNeedMobile', {
        is: true,
        then: string()
          .required('กรุณากรอกหมายเลขโทรศัพท์มือถือ')
          .test('mobileNo-length-test', 'กรอกได้ไม่เกิน 10 ตัวอักษร', (val) => {
            return val?.length <= 10
          })
          .test(
            'mobile-test',
            'กรุณากรอกหมายเลขโทรศัพท์มือถือให้ถูกต้อง',
            (val) => {
              return val?.length == 10 && /^[0-9]+$/.test(val)
            }
          ),
        otherwise: string().nullable(),
      }),
    birthDate: string()
      .default('')
      .when('$isNeedBirthDate', {
        is: true,
        then: string()
          .required('กรุณาเลือกวัน เดือน ปี เกิด')
          .test('date-format-test', 'กรุณากรอกวันที่ให้ถูกต้อง', (val) => {
            return isValidateDate(val)
          })
          .test(
            'date-before-today',
            'กรุณากรอกวันที่เกิดที่ผ่านมาแล้ว',
            (val) => isDateBeforeToday(val)
          ),
        otherwise: string().nullable(),
      }),
    zipCode: string()
      .default('')
      .when('$isAllFieldsRequired', {
        is: true,
        then: string()
          .required('กรุณากรอกรหัสไปรษณีย์')
          .test('zipCode-length-test', 'กรอกได้ไม่เกิน 5 ตัวอักษร', (val) => {
            return val?.length <= 5
          }),
        otherwise: string()
          .test('zipCode-length-test', 'กรอกได้ไม่เกิน 5 ตัวอักษร', (val) => {
            return !val || val?.length <= 5
          })
          .nullable(),
      }),
    province: string()
      .default('')
      .when('$isAllFieldsRequired', {
        is: true,
        then: string().required('กรุณากรอกจังหวัด'),
        otherwise: string().nullable(),
      }),
    amphure: string()
      .default('')
      .when('$isAllFieldsRequired', {
        is: true,
        then: string().required('กรุณากรอกอำเภอ/เขต'),
        otherwise: string().nullable(),
      }),
    tambon: string()
      .default('')
      .when('$isAllFieldsRequired', {
        is: true,
        then: string().required('กรุณากรอกตำบล/แขวง'),
        otherwise: string().nullable(),
      }),
    address: string()
      .default('')
      .when('$isAllFieldsRequired', {
        is: true,
        then: string()
          .required('กรุณากรอกรายละเอียดที่อยู่')
          .test('address-length-test', 'กรอกได้ไม่เกิน 255 ตัวอักษร', (val) => {
            return val?.length <= 255
          }),
        otherwise: string()
          .test('address-length-test', 'กรอกได้ไม่เกิน 255 ตัวอักษร', (val) => {
            return !val || val?.length <= 255
          })
          .nullable(),
      }),
    institution: string()
      .default('')
      .when('$isAllFieldsRequired', {
        is: true,
        then: string()
          .required('กรุณากรอกสถานศึกษา')
          .test(
            'institution-length-test',
            'กรอกได้ไม่เกิน 255 ตัวอักษร',
            (val) => {
              return val?.length <= 255
            }
          ),
        otherwise: string()
          .test(
            'institution-length-test',
            'กรอกได้ไม่เกิน 255 ตัวอักษร',
            (val) => {
              return !val || val?.length <= 255
            }
          )
          .nullable(),
      }),
    qualification: string()
      .default('')
      .when('$isAllFieldsRequired', {
        is: true,
        then: string().required('กรุณาเลือกวุฒิการศึกษา'),
        otherwise: string().nullable(),
      }),
    branch: string()
      .default('')
      .when('$isAllFieldsRequired', {
        is: true,
        then: string()
          .required('กรุณากรอกสาขา')
          .test('branch-length-test', 'กรอกได้ไม่เกิน 255 ตัวอักษร', (val) => {
            return val?.length <= 255
          }),
        otherwise: string()
          .test('branch-length-test', 'กรอกได้ไม่เกิน 255 ตัวอักษร', (val) => {
            return !val || val?.length <= 255
          })
          .nullable(),
      }),
    startYear: string()
      .required('กรุณากรอกปีเริ่มต้นให้ถูกต้อง')
      .test('startYear-format', 'กรุณากรอกปี 4 หลัก เช่น 2000', (val) => {
        console.log('val ->>', val)
        if (val === '' || val === null || val === '-' || val === 'null')
          return true
        return /^\d{4}$/.test(val)
      })
      .test('startYear-min', 'ปีเริ่มต้นต้องไม่ต่ำกว่า 1900', (val) => {
        if (val === '' || val === null || val === '-' || val === 'null')
          return true
        return parseInt(val, 10) >= 1900
      })
      .test('startYear-max', 'ปีเริ่มต้นต้องไม่มากกว่าปีปัจจุบัน', (val) => {
        if (val === '' || val === null || val === '-' || val === 'null')
          return true
        return parseInt(val, 10) <= new Date().getFullYear()
      }),

    endYear: string()
      .required('กรุณากรอกปีสิ้นสุดให้ถูกต้อง')
      .test('endYear-format', 'กรุณากรอกปี 4 หลัก เช่น 2000', (val) => {
        if (val === '' || val === null || val === '-' || val === 'null')
          return true
        return /^\d{4}$/.test(val)
      })
      .test('endYear-min', 'ปีสิ้นสุดต้องไม่ต่ำกว่า 1900', (val) => {
        if (val === '' || val === null || val === '-' || val === 'null')
          return true
        return parseInt(val, 10) >= 1900
      })
      .test('endYear-max', 'ปีสิ้นสุดต้องไม่มากกว่าปีปัจจุบัน', (val) => {
        if (val === '' || val === null || val === '-' || val === 'null')
          return true
        return parseInt(val, 10) <= new Date().getFullYear()
      })
      .when('startYear', (startYear, schema) => {
        if (!startYear || !/^\d{4}$/.test(startYear)) return schema
        return schema.test(
          'endYear-after-startYear',
          'ปีสิ้นสุดต้องไม่น้อยกว่าปีเริ่มต้น',
          (val) => {
            if (
              val === '' ||
              val === null ||
              val === '-' ||
              val === 'null' ||
              !/^\d{4}$/.test(val)
            )
              return true
            return parseInt(val, 10) >= parseInt(startYear, 10)
          }
        )
      }),
    gpa: string()
      .default('')
      .test('gpa-length-test', 'กรอกได้ไม่เกิน 255 ตัวอักษร', (val) => {
        return !val || val?.length <= 255
      })
      .nullable(),
  })
  try {
    await validateInputSchema.validate(currentState, {
      abortEarly: false,
      context: {
        isAgent,
        isStaffAcademy,
        isStaffNonAcademy,
        isAllFieldsRequired,
        isNeedIdCard:
          isStaffAcademy || isStaffNonAcademy || isProspect || nonAcademy,
        isNeedMobile: isAllFieldsRequired || nonAcademy,
        isNeedBirthDate:
          isStaffAcademy || isStaffNonAcademy || isProspect || nonAcademy,
      },
    })
    listenerApi.dispatch(setFormError({ index: index, value: [] }))
    if (originalState.examinersFormError[index]) {
      saveDebounce(() =>
        listenerApi.dispatch(
          setExaminersFormError({ index: index, value: false })
        )
      )
    }
  } catch (e) {
    listenerApi.dispatch(setFormError({ index: index, value: e.inner }))
    if (!originalState.examinersFormError[index]) {
      saveDebounce(() =>
        listenerApi.dispatch(
          setExaminersFormError({ index: index, value: true })
        )
      )
    }
  }
}

export function handleStep1Button(listenerApi) {
  const selectedRounds = listenerApi.getState().eExamination.selectedRounds
  const examinerLength = listenerApi.getState().eExamination.examiners.length
  if (selectedRounds.length > 0 && examinerLength > 0) {
    const { available } = _.minBy(selectedRounds, (round) => {
      return round.available
    })
    if (examinerLength <= available) {
      listenerApi.dispatch(
        setReduxValue({ index: 'nextButtonDisabled', value: false })
      )
    } else {
      listenerApi.dispatch(
        setReduxValue({ index: 'nextButtonDisabled', value: true })
      )
    }
  } else {
    listenerApi.dispatch(
      setReduxValue({ index: 'nextButtonDisabled', value: true })
    )
  }
}

export function handleStep2Button(listenerApi) {
  const formNextButtonDisabled =
    listenerApi.getState().eExamination.formNextButtonDisabled
  const consent = listenerApi.getState().eExamination.consent
  const errorCheck = _.findIndex(formNextButtonDisabled, (ele) => {
    return ele
  })
  if (errorCheck >= 0 || !consent) {
    listenerApi.dispatch(
      setReduxValue({ index: 'nextButtonDisabled', value: true })
    )
  } else if (consent) {
    listenerApi.dispatch(
      setReduxValue({ index: 'nextButtonDisabled', value: false })
    )
  }
}

export function handleStep3Button(listenerApi) {
  const { progress, consent } = listenerApi.getState().eExamination
  if (progress === 2) {
    listenerApi.dispatch(
      setReduxValue({ index: 'nextButtonDisabled', value: !consent })
    )
  }
}

export const manageOtherDocsName = (oldDocs, newDoc) => {
  // add number to name
  let myArray = []
  if (newDoc) {
    myArray = [...oldDocs, newDoc]
  } else {
    myArray = [...oldDocs]
  }
  const groupDocs = []
  const myArray_ = myArray.map((item) => {
    if (!groupDocs[item.documentName]) {
      groupDocs[item.documentName] = 0
    }
    return {
      ...item,
      type: item.documentName,
    }
  }, {})
  const newArr = myArray_.map((item) => {
    groupDocs[item.type] = groupDocs[item.type] + 1
    return {
      ...item,
      order: groupDocs[item.type],
    }
  })
  return newArr
}
